/**
 * Exception Options Service
 * 
 * Service for fetching available exception configuration options
 * including components, templates, and tagging functions.
 */

import { apiClient } from '../api'

export interface ComponentOption {
  name: string
  description: string
  domain: string
}

export interface TemplateOption {
  name: string
  description: string
  is_default: boolean
}

export interface ExceptionOptionsResponse {
  components?: ComponentOption[]
  templates?: TemplateOption[]
  functions?: string[]
  domains?: string[]
  count: number
}

export interface DqTeamMember {
  id: number
  name: string
  email: string
  role: string
}

export interface DqTeamMembersResponse {
  members: DqTeamMember[]
  count: number
}

// Cache for options to avoid repeated API calls
const optionsCache: {
  components?: ExceptionOptionsResponse
  templates?: ExceptionOptionsResponse
  functions?: ExceptionOptionsResponse
  domains?: ExceptionOptionsResponse
  lastFetch?: number
} = {}

const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Check if cache is still valid
 */
const isCacheValid = (): boolean => {
  if (!optionsCache.lastFetch) return false
  return Date.now() - optionsCache.lastFetch < CACHE_DURATION
}

/**
 * Fetch available React components for exception display
 */
export const fetchAvailableComponents = async (): Promise<ExceptionOptionsResponse> => {
  if (isCacheValid() && optionsCache.components) {
    return optionsCache.components
  }

  try {
    const response = await apiClient.get('/admin/exception-options/components')
    optionsCache.components = response.data
    optionsCache.lastFetch = Date.now()
    return response.data
  } catch (error) {
    console.error('Failed to fetch available components:', error)
    throw error
  }
}

/**
 * Fetch available report templates
 */
export const fetchAvailableTemplates = async (): Promise<ExceptionOptionsResponse> => {
  if (isCacheValid() && optionsCache.templates) {
    return optionsCache.templates
  }

  try {
    const response = await apiClient.get('/admin/exception-options/templates')
    optionsCache.templates = response.data
    optionsCache.lastFetch = Date.now()
    return response.data
  } catch (error) {
    console.error('Failed to fetch available templates:', error)
    throw error
  }
}

/**
 * Fetch available tagging functions
 */
export const fetchAvailableFunctions = async (): Promise<ExceptionOptionsResponse> => {
  if (isCacheValid() && optionsCache.functions) {
    return optionsCache.functions
  }

  try {
    const response = await apiClient.get('/admin/exception-options/functions')
    optionsCache.functions = response.data
    optionsCache.lastFetch = Date.now()
    return response.data
  } catch (error) {
    console.error('Failed to fetch available functions:', error)
    throw error
  }
}

/**
 * Fetch available data domains
 */
export const fetchDataDomains = async (): Promise<ExceptionOptionsResponse> => {
  if (isCacheValid() && optionsCache.domains) {
    return optionsCache.domains
  }

  try {
    const response = await apiClient.get('/admin/exception-options/domains')
    optionsCache.domains = response.data
    optionsCache.lastFetch = Date.now()
    return response.data
  } catch (error) {
    console.error('Failed to fetch data domains:', error)
    throw error
  }
}

/**
 * Fetch active users eligible to own an exception type (EVO_ADMIN / EVO_TEAM).
 * Not cached - membership changes independently of the static option lists.
 */
export const fetchDqTeamMembers = async (): Promise<DqTeamMembersResponse> => {
  try {
    const response = await apiClient.get('/admin/exception-options/dq-team-members')
    return response.data
  } catch (error) {
    console.error('Failed to fetch DQ team members:', error)
    throw error
  }
}

/**
 * Clear the options cache
 */
export const clearOptionsCache = (): void => {
  optionsCache.components = undefined
  optionsCache.templates = undefined
  optionsCache.functions = undefined
  optionsCache.domains = undefined
  optionsCache.lastFetch = undefined
}

/**
 * Fetch all options at once
 */
export const fetchAllOptions = async () => {
  const [components, templates, functions, domains] = await Promise.all([
    fetchAvailableComponents(),
    fetchAvailableTemplates(),
    fetchAvailableFunctions(),
    fetchDataDomains()
  ])

  return {
    components,
    templates,
    functions,
    domains
  }
}